import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

const SectionReviews = ({ section }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const trackRef = useRef(null);
  const containerRef = useRef(null);

  const allItems = section.items || [];
  const items = allItems;
  const gap = 24;

  const updateCardWidth = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;

    if (containerWidth >= 1024) {
      // Desktop: show ~2.5 cards
      setCardWidth((containerWidth - gap * 2) / 2.5);
    } else if (containerWidth >= 640) {
      // Tablet: show ~1.5 cards
      setCardWidth((containerWidth - gap) / 1.5);
    } else {
      // Mobile: show 1 card with peek
      setCardWidth(containerWidth - 48);
    }
  }, []);

  useEffect(() => {
    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, [updateCardWidth]);

  const visibleCards = cardWidth > 0
    ? Math.floor(containerRef.current?.offsetWidth / (cardWidth + gap)) || 1
    : 1;
  const maxIndex = Math.max(0, items.length - visibleCards);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  // Touch/swipe support
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (diff > threshold) {
      handleNext();
    } else if (diff < -threshold) {
      handlePrev();
    }
  };

  const translateX = -(currentIndex * (cardWidth + gap));

  if (allItems.length === 0) return null;

  return (
    <div className={`w-full py-8 lg:py-10 bg-white ${section.customClasses || ''}`}>
      {section.header && (
        <h2
          className={`text-center mb-8 lg:mb-10 px-8 text-[#1C2938] ${
            section.headerClasses || 'text-4xl lg:text-5xl font-bold'
          }`}
        >
          {parse(section.header)}
        </h2>
      )}

      <div
        ref={containerRef}
        className="overflow-hidden px-6 lg:px-12 xl:px-24"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={trackRef}
          className="flex transition-transform duration-300 ease-in-out"
          style={{
            transform: `translateX(${translateX}px)`,
            gap: `${gap}px`,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="flex-shrink-0 bg-white rounded-lg p-6 lg:p-8 flex flex-col justify-between"
              style={{ width: `${cardWidth}px` }}
            >
              {item.image?.url && (
                <div className="mb-4">
                  <img
                    src={item.image.url}
                    alt="Rating"
                    className="h-5 object-contain"
                  />
                </div>
              )}

              <div className="flex-1 text-[#1C2938] text-base lg:text-lg leading-relaxed mb-6">
                {parse(item.body || '')}
              </div>

              {item.header && (
                <div className="text-[#1C2938] text-base">
                  {parse(item.header)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {items.length > 1 && (
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="w-10 h-10 rounded-full border-2 border-[#1C2938] flex items-center justify-center
              transition-opacity disabled:opacity-30 hover:bg-gray-100"
            aria-label="Previous review"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="#1C2938" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
            className="w-10 h-10 rounded-full border-2 border-[#1C2938] flex items-center justify-center
              transition-opacity disabled:opacity-30 hover:bg-gray-100"
            aria-label="Next review"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 4L10 8L6 12" stroke="#1C2938" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

    </div>
  );
};

SectionReviews.propTypes = {
  section: PropTypes.shape({
    typeOf: PropTypes.string.isRequired,
    header: PropTypes.string,
    headerClasses: PropTypes.string,
    body: PropTypes.string,
    customClasses: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        header: PropTypes.string,
        body: PropTypes.string,
        image: PropTypes.shape({
          url: PropTypes.string,
        }),
      })
    ).isRequired,
  }).isRequired,
};

export default SectionReviews;
